import { Box, Grid, SxProps, useTheme, useMediaQuery } from '@mui/material';
import moment from 'moment';
import React, { Dispatch, ReactNode, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import CAppPaper from '../../../../app/components/CAppPaper';
import AppFormatAmount from '../../../../app/features/format/AppFormatAmount';
import AppFormatPercent from '../../../../app/features/format/AppFormatPercent';
import AppFormatX from '../../../../app/features/format/AppFormatX';
import CDateUpdatedAtom from '../../components/CDateUpdatedAtom';
import langInvestWordLang from '../../langs/langInvestWordLang';
import IFund from '../../interfaces/fund/IFund';
import useInvestFundPerformances from '../../hooks/fund/useInvestFundPerformances';
import IFundFilters from '../../interfaces/fund/IFundFilters';
import langInvestFundPerformancesVentureCapitalLang from '../../langs/langInvestFundPerformancesVentureCapitalLang';

type VInvestFundPerformanceVentureCapitalProps = {
	fund: IFund;
	filters: IFundFilters;
	setDataUpdatedOn: Dispatch<Date>;
};

const VInvestFundPerformanceVentureCapital = (props: VInvestFundPerformanceVentureCapitalProps) => {
	const { fund, filters, setDataUpdatedOn } = props;

	const { capital, performance, portfolio } = useInvestFundPerformances(fund ? fund.id : null, filters);
	const { t } = useTranslation();
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

	useEffect(() => {
		capital && setDataUpdatedOn(capital.dataUpdatedOn);
	}, [capital, setDataUpdatedOn]);

	const styleTitle = {
		color: theme.palette.secondary.main,
		//verticalAlign: 'text-bottom',
		boxSizing: 'inherit',
		textAlign: 'left',
		//	alignItems: 'bottom',
		fontWeight: '500',
		fontSize: '1.25rem',
		lineHeight: '2rem',
		'& .icon': {
			marginRight: '10px',
		},
		'& small': {
			fontSize: '0.8rem',
		},
	};
	const styleKPI = {
		padding: '30px',
		// background: `url(${AppBackgroundTriangleBgDarkBlueSrc}), ${theme.palette.primary.main}`,
		// backgroundSize: '50% 50%',
		// backgroundRepeat: 'no-repeat',
		// backgroundPosition: 'top right',
		border: '0px',
		borderRadius: '10px',
		textAlign: 'left',
		'& .title': styleTitle,
		'& .title-position': {
			color: '#FFF',
			lineHeight: isMobile ? '1.5' : '2rem',
			fontWeight: '600',
			fontSize: '0.9rem',
			textAlign: 'right',
		},
		'& .title-total': {
			color: '#FFF',
			fontWeight: '400',
			fontSize: '0.9rem',
			textAlign: 'right',
			lineHeight: isMobile ? '1.5' : '2rem',
		},
		'& .list': {
			color: 'white',
			textAlign: 'left',
			alignItems: 'top',
			padding: '10px 0px 0px 0px',
			fontWeight: 'normal',
			'& .item': {
				color: 'white',
				textAlign: 'left',
				alignItems: 'center',
				padding: '6px 0px',
				minHeight: '3rem',
				fontWeight: 'normal',
				'& .value': {
					textAlign: 'right',
					fontWeight: '600',
					fontSize: isMobile ? '0.8rem' : '1.25rem',
				},
				'& .total': {
					textAlign: 'right',
					fontWeight: '400',
					fontSize: isMobile ? '0.8rem' : '1.25rem',
					//lineHeight: '2.4rem',
				},
				'& .label': {
					textAlign: 'left',
					fontSize: isMobile ? '0.9rem' : '1rem',
				},
			},
			'& .item:last-child': {
				paddingBottom: '0px',
			},
		},
	} as SxProps;

	return (
		<>
			<Grid container spacing={3}>
				<Grid item sm={5} xs={12}>
					<CAppPaper
						id="fund-performance-capital-block"
						variant="outlined"
						displayBackgroundImage
						color="primary"
						sx={styleKPI}
					>
						<Grid container spacing={3}>
							<Grid item sm={6} xs={12}>
								<Box className="title">
									{t(langInvestFundPerformancesVentureCapitalLang.title_capital.tag)}
									&nbsp;
									<small>
										<CDateUpdatedAtom
											isEstimated={capital?.isEstimated}
											date={
												capital &&
												moment(moment(capital.dataUpdatedOn).format('YYYY-MM-DD')).toDate()
											}
											dateOnly
										/>{' '}
										|{' '}
										<i>
											{capital && capital.isEstimated
												? t(langInvestWordLang.estimated.tag)
												: t(langInvestWordLang.audited.tag)}
										</i>
									</small>
								</Box>
							</Grid>
							<Grid item xs={6} sx={{ display: { sm: 'none' } }} />
							<Grid item xs={3} sm={3}>
								<Box className="title-position">
									{t(langInvestFundPerformancesVentureCapitalLang.text_your_position.tag)}
								</Box>
							</Grid>
							<Grid item xs={3}>
								<Box className="title-total">
									{t(langInvestFundPerformancesVentureCapitalLang.text_fund_total.tag)}
								</Box>
							</Grid>
						</Grid>
						<Box className="list">
							<CapitalKpiLine
								label={t(langInvestFundPerformancesVentureCapitalLang.committed_capital.tag)}
								value={
									<AppFormatAmount
										value={capital?.kpis.committed?.value}
										addSpace
										textIfNull={t(langInvestWordLang.nc.tag)}
										currency={fund.currency}
									/>
								}
								total={
									<AppFormatAmount
										value={capital?.kpis.committed?.total}
										addSpace
										textIfNull={t(langInvestWordLang.nc.tag)}
										currency={fund.currency}
									/>
								}
							/>
							<CapitalKpiLine
								label={t(langInvestFundPerformancesVentureCapitalLang.called_capital.tag)}
								value={
									<AppFormatAmount
										value={capital?.kpis.called?.value}
										addSpace
										textIfNull={t(langInvestWordLang.nc.tag)}
										currency={fund.currency}
									/>
								}
								total={
									<AppFormatAmount
										value={capital?.kpis.called?.total}
										addSpace
										textIfNull={t(langInvestWordLang.nc.tag)}
										currency={fund.currency}
									/>
								}
							/>
							<CapitalKpiLine
								label={<>{t(langInvestFundPerformancesVentureCapitalLang.callable_capital.tag)}</>}
								value={
									<AppFormatAmount
										value={capital?.kpis.callable?.value}
										addSpace
										textIfNull={t(langInvestWordLang.nc.tag)}
										currency={fund.currency}
									/>
								}
								total={
									<AppFormatAmount
										value={capital?.kpis.callable?.total}
										addSpace
										textIfNull={t(langInvestWordLang.nc.tag)}
										currency={fund.currency}
									/>
								}
							/>
							<CapitalKpiLine
								label={t(langInvestFundPerformancesVentureCapitalLang.distributions.tag)}
								value={
									<AppFormatAmount
										value={capital?.kpis.distributions?.value}
										addSpace
										textIfNull={t(langInvestWordLang.nc.tag)}
										currency={fund.currency}
									/>
								}
								total={
									<AppFormatAmount
										value={capital?.kpis.distributions?.total}
										addSpace
										textIfNull={t(langInvestWordLang.nc.tag)}
										currency={fund.currency}
									/>
								}
							/>
							<CapitalKpiLine
								label={t(langInvestFundPerformancesVentureCapitalLang.distributions_recallable.tag)}
								value={
									<AppFormatAmount
										value={capital?.kpis.distributionsRecallable?.value}
										addSpace
										textIfNull={t(langInvestWordLang.na.tag)}
										currency={fund.currency}
									/>
								}
								total={
									<AppFormatAmount
										value={capital?.kpis.distributionsRecallable?.total}
										addSpace
										textIfNull={t(langInvestWordLang.na.tag)}
										currency={fund.currency}
									/>
								}
							/>
							<CapitalKpiLine
								label={t(langInvestFundPerformancesVentureCapitalLang.fees.tag)}
								value={
									<AppFormatAmount
										value={capital?.kpis.fees?.value}
										addSpace
										textIfNull={t(langInvestWordLang.nc.tag)}
										currency={fund.currency}
									/>
								}
								total={
									<AppFormatAmount
										value={capital?.kpis.fees?.total}
										addSpace
										textIfNull={t(langInvestWordLang.nc.tag)}
										currency={fund.currency}
									/>
								}
							/>
						</Box>
					</CAppPaper>
				</Grid>
				<Grid item sm={3} xs={12}>
					<CAppPaper
						id="fund-performance-kpis-block"
						variant="outlined"
						displayBackgroundImage
						color="primary"
						sx={styleKPI}
					>
						<Grid container spacing={3}>
							<Grid item sm={6} xs={12}>
								<Box className="title">
									{t(langInvestFundPerformancesVentureCapitalLang.title_fund.tag)}
								</Box>
							</Grid>
							<Grid item xs={7} sx={{ display: { sm: 'none' } }} />
							<Grid item xs={5} sm={6}>
								<Box className="title-position">
									{t(langInvestFundPerformancesVentureCapitalLang.text_fund_total.tag)}
								</Box>
							</Grid>
						</Grid>

						<Box className="list">
							<PerformanceKpiLine
								label={<>{t(langInvestFundPerformancesVentureCapitalLang.tvpi.tag)}</>}
								value={
									<AppFormatX
										value={performance?.kpisTotal.tvpi}
										textIfNull={t(langInvestWordLang.nc.tag)}
									/>
								}
							/>
							<PerformanceKpiLine
								label={t(langInvestFundPerformancesVentureCapitalLang.dpi.tag)}
								value={
									<AppFormatX
										value={
											performance && performance.kpisTotal ? performance.kpisTotal.dpi : undefined
										}
										textIfNull={t(langInvestWordLang.nc.tag)}
									/>
								}
							/>
							<PerformanceKpiLine
								label={t(langInvestFundPerformancesVentureCapitalLang.rvpi.tag)}
								value={
									<AppFormatX
										value={
											performance && performance.kpisTotal
												? performance.kpisTotal.rvpi
												: undefined
										}
										textIfNull={t(langInvestWordLang.nc.tag)}
									/>
								}
							/>
							<PerformanceKpiLine
								label={t(langInvestFundPerformancesVentureCapitalLang.irr_gross.tag)}
								value={
									<AppFormatPercent
										value={
											performance && performance.kpisTotal
												? performance.kpisTotal.irrGross
												: undefined
										}
										isN={
											performance && performance.kpisTotal
												? performance.kpisTotal.irrGrossIsN
												: null
										}
										textIfNull={t(langInvestWordLang.nc.tag)}
									/>
								}
							/>
							<PerformanceKpiLine
								label={t(langInvestFundPerformancesVentureCapitalLang.irr.tag)}
								value={
									<AppFormatPercent
										value={performance?.kpisTotal.irr ?? undefined}
										isNS={performance?.kpisTotal.irrIsNs ?? undefined}
										textIfNull={t(langInvestWordLang.nc.tag)}
									/>
								}
							/>
							<PerformanceKpiLine
								label={t(langInvestFundPerformancesVentureCapitalLang.nav.tag)}
								value={
									<AppFormatAmount
										value={performance?.kpisTotal.netAssetValue ?? undefined}
										addSpace
										textIfNull={t(langInvestWordLang.nc.tag)}
										currency={performance?.kpisTotal?.currency}
									/>
								}
							/>
						</Box>
					</CAppPaper>
				</Grid>
				<Grid item sm={4} xs={12}>
					<CAppPaper
						id="fund-performance-portfolio-block"
						variant="outlined"
						displayBackgroundImage
						color="primary"
						sx={styleKPI}
					>
						<Grid container spacing={3}>
							<Grid item sm={6} xs={12}>
								<Box className="title">
									{t(langInvestFundPerformancesVentureCapitalLang.title_portfolio.tag)}
								</Box>
							</Grid>
							<Grid item xs={7} sx={{ display: { sm: 'none' } }} />
							<Grid item xs={5} sm={6}>
								<Box className="title-position">
									{t(langInvestFundPerformancesVentureCapitalLang.text_fund_total.tag)}
								</Box>
							</Grid>
						</Grid>

						<Box className="list">
							<PerformanceKpiLine
								label={t(langInvestFundPerformancesVentureCapitalLang.capital_invested.tag)}
								value={
									<AppFormatAmount
										value={portfolio?.kpis?.capitalInvested}
										addSpace
										textIfNull={t(langInvestWordLang.nc.tag)}
										currency={portfolio?.kpis?.currency}
									/>
								}
							/>
							<PerformanceKpiLine
								label={t(langInvestFundPerformancesVentureCapitalLang.realized_gains.tag)}
								value={
									<AppFormatAmount
										value={portfolio?.kpis?.realizedGains}
										addSpace
										textIfNull={t(langInvestWordLang.nc.tag)}
										currency={portfolio?.kpis?.currency}
									/>
								}
							/>

							<PerformanceKpiLine
								label={t(langInvestFundPerformancesVentureCapitalLang.unrealized_gains.tag)}
								value={
									<AppFormatAmount
										value={portfolio?.kpis?.unrealizedGains}
										addSpace
										textIfNull={t(langInvestWordLang.nc.tag)}
										currency={portfolio?.kpis?.currency}
									/>
								}
							/>
							<PerformanceKpiLine
								label={t(
									langInvestFundPerformancesVentureCapitalLang.percent_of_capital_called_invested.tag,
								)}
								labelSub={t(
									langInvestFundPerformancesVentureCapitalLang.percent_of_capital_called_invested_sub
										.tag,
								)}
								value={
									<AppFormatPercent
										value={portfolio?.kpis?.percentOfCapitalCalledInvested}
										addSpace
										textIfNull={t(langInvestWordLang.nc.tag)}
									/>
								}
							/>
							<PerformanceKpiLine
								label={t(langInvestFundPerformancesVentureCapitalLang.total_value.tag)}
								labelSub={t(langInvestFundPerformancesVentureCapitalLang.total_value_sub.tag)}
								value={
									<AppFormatAmount
										value={portfolio?.kpis?.totalValue}
										addSpace
										textIfNull={t(langInvestWordLang.nc.tag)}
										currency={portfolio?.kpis?.currency}
									/>
								}
							/>
							<PerformanceKpiLine
								label={t(langInvestFundPerformancesVentureCapitalLang.moic.tag)}
								value={
									<AppFormatX
										value={portfolio?.kpis?.moic}
										textIfNull={t(langInvestWordLang.nc.tag)}
									/>
								}
							/>
						</Box>
					</CAppPaper>
				</Grid>
			</Grid>
		</>
	);
};

type CapitalKpiLineProps = {
	label: string | ReactNode;
	labelSub?: string | ReactNode;
	value: string | ReactNode;
	total: string | ReactNode;
};
const CapitalKpiLine = (props: CapitalKpiLineProps) => {
	const { label, labelSub, value, total } = props;

	return (
		<Box className="item">
			<Grid container spacing={3} alignItems="center" justifyContent="top">
				<Grid item xs={6} className="label">
					<Box>
						<Box>{label}</Box>
						{labelSub ? <Box sx={{ lineHeight: '1.2rem', fontSize: '0.8rem' }}>{labelSub}</Box> : ''}
					</Box>
				</Grid>
				<Grid item xs={3} className="value">
					{value}
				</Grid>
				<Grid item xs={3} className="total">
					{total}
				</Grid>
			</Grid>
		</Box>
	);
};

type PerformanceKpiLineProps = {
	label: string | ReactNode;
	labelSub?: string | ReactNode;
	value: string | ReactNode;
};
const PerformanceKpiLine = (props: PerformanceKpiLineProps) => {
	const { label, value, labelSub } = props;

	return (
		<Box className="item">
			<Grid container spacing={3} alignItems="center" justifyContent="top">
				<Grid item xs={6} className="label">
					<Box>{label}</Box>
					{labelSub ? <Box sx={{ lineHeight: '1.2rem', fontSize: '0.8rem' }}>{labelSub}</Box> : ''}
				</Grid>
				<Grid item xs={6} className="value">
					{value}
				</Grid>
			</Grid>
		</Box>
	);
};

export default VInvestFundPerformanceVentureCapital;
